import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Popconfirm,
  message,
  Tag,
  Radio,
  Tooltip,
  Select,
  Popover
} from "antd";
import { Helmet } from "react-helmet";
import startCase from "lodash/startCase";
import capitalize from "lodash/capitalize";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  SyncOutlined,
  FunnelPlotOutlined,
} from "@ant-design/icons";
import MediaForm from "../components/Library/MediaForm";
import axios from "axios";
import dayjs from "dayjs";
import Link from "antd/es/typography/Link";
import { getMediaIcon } from "../utils/Misc";
import PreviewMedia from "../components/Library/PreviewMedia";
import RejectButton from "../components/Common/RejectButton";
import RequirePermission from "../components/Common/RequirePermission";

const { Title } = Typography;
const { Option } = Select;

const Library = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const initialStatus = queryParams.get("status") || "1";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [media, setMedia] = useState([]);
  const [editingMedia, setEditingMedia] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [approvePermission, setApprovePermission] = useState(false);
  const [createPermission, setCreatePermission] = useState(false);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [activeStatus, setActiveStatus] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [syncLoading, setSyncLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState("");
  const setQueryParams = (params) => {
    const searchParams = new URLSearchParams(params);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilterText(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value) => {
    setActiveStatus(value);
    const newQueryParams = {
      status: selectedStatus,
      activeStatus: value,
      type: selectedType
    };
    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    const newQueryParams = {
      status: selectedStatus,
      activeStatus: activeStatus,
      type: value,
    };
    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };  

  const handleTableChange = (pagination, filters, sorter) => {
    // if (typeof sorter.order === "undefined") {
    //   setSortingOrder("desc");
    //   setSortingColumn("id");
    // } else {
    //   setSortingColumn(sorter.field);
    //   setSortingOrder(sorter.order === "ascend" ? "asc" : "desc");
    // }
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleEdit = (record) => {
    setIsModalOpen(true);
    setEditingMedia(record);
  };

  const handlePreviewMedia = (record) => {
    setIsPreviewModalOpen(true);
    setPreviewMedia(record);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/media/${id}`
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Media deleted");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/api/media/approve/${id}`
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Media approved");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleReject = async (id, reason) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/media/reject/${id}`,
        {
          reason: reason,
        }
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Media rejected");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSync = async () => {
    try {
      setSyncLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/media/sync`
      );
      message.success("Media synced successfully");
      await fetchData();
      setSyncLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/media`,
        {
          params: {
            filter: filterText, // add filter if needed
            limit: pageSize,
            page: currentPage,
            status: selectedStatus,
            activeStatus: activeStatus,
            type: selectedType
          },
        }
      );
      let records = response.data.data.data;
      setTotalRecords(response.data.data.total);
      setPendingTotal(response.data.data.pending_total);
      setMedia(records);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching media data", error);
    }
  };

  const handleApprovalStatus = (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
    const newQueryParams = {
      status: newStatus,
    };
    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  useEffect(() => {
    const newQueryParams = {
      status: selectedStatus,
      activeStatus: activeStatus,
      type: selectedType
    };

    if (JSON.stringify(newQueryParams) !== JSON.stringify(Object.fromEntries(new URLSearchParams(location.search)))) {
      setQueryParams(newQueryParams);
    };

    setQueryParams(newQueryParams);
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(fetchTimer);
  }, [currentPage, pageSize, filterText, selectedStatus, activeStatus, selectedType]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    setUser(storedUser);
    const permissions = storedUser.permissions;
    setApprovePermission(parseInt(permissions["signage.media_approve"]) === 1);
    setCreatePermission(parseInt(permissions["signage.media_create"]) === 1);
    setDeletePermission(parseInt(permissions["signage.media_delete"]) === 1);
    setEditPermission(parseInt(permissions["signage.media_edit"]) === 1);
  }, []);

  const popOverColumns = [
    {
      title: "Id",
      dataIndex: "id",
      render: (text) => <span className="text-small">{startCase(text)}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <span className="text-small">{text?.toUpperCase()}</span>,
    },
  ];

  const columns = [
    { title: "Title", dataIndex: "title" },
    {
      title: "Media",
      dataIndex: "media_type",
      render: (text, record) => (
        <Link href="#" onClick={() => handlePreviewMedia(record)}>
          <Tag>{getMediaIcon(text)} Preview</Tag>
        </Link>
      ),
    }];
  if (selectedStatus === "1") {
    columns.push(
      {
        title: "Times played",
        dataIndex: "total_play_count",
        render: (text) => (
          <span style={{ alignContent: "center" }}>
            {text ? text : 0}
          </span>
        ),
      }
    )
  };
  columns.push({
    title: "File Size",
    dataIndex: "file_data",
    render: (text, record) => {
      let size = "-";
      if (record.file_data) {
        const fileData = JSON.parse(record.file_data);
        size = fileData.file_size;
        let len = fileData.file_size.length;
        fileData.file_size = fileData.file_size.substring(0, len - 3);
        if (parseFloat(fileData.file_size) > 1024) {
          const sz = parseFloat(fileData.file_size) / 1024;
          size = sz.toFixed(2) + " MB";
        }
      }
      return size;
    },
  });
  columns.push({
    title: "Status",
    dataIndex: "status",
    render: (status) => (
      <Tag color={status === "active" ? "green" : "red"}>
        {capitalize(status)}
      </Tag>
    ),
  })
  if (selectedStatus === "1") {
    columns.push({
      title: "Approved At",
      dataIndex: "approved_at",
      render: (text) => (
        <span className="text-small">
          {dayjs(text).format("DD-MM-YYYY HH:mm")}
        </span>
      ),
    });
    columns.push({
      title: "Approved By",
      dataIndex: "approved_by_user",
      render: (text) => {
        return <span className="text-small">{startCase(text)}</span>;
      },
    });
  }
  if (selectedStatus === "0") {
    columns.push({
      title: "Approval List",
      dataIndex: "approver_ids",
      render: (text, record) => {
        const len = record.approver_ids.length;
        if (len === 0) {
          return <Tag color="red">No Approver Assigned</Tag>;
        }

        if (len === 1) {
          return <Tag color="green">{record.approver_ids[0].name}</Tag>;
        }
        const sortedApprovers = [...record.approver_ids];
        return (
          <Popover
            placement="right"
            content={
              <Table
                loading={loading}
                columns={popOverColumns}
                dataSource={sortedApprovers}
                size="small"
                pagination={false}
                scroll={{ y: 400 }}
                style={{ width: "300px" }}
                rowKey="id"
              />
            }
            title="Assigned Approver List"
            trigger="hover"
          >
            <Tag style={{ cursor: "pointer" }} color="blue">
              {`${len} Approvers`}
            </Tag>
          </Popover>
        );
      },
    });
  }
  if (selectedStatus === "1" || selectedStatus === "0") {
    columns.push({
      title: "Updated At",
      dataIndex: "modified_at",
      render: (text) => (
        <span className="text-small">
          {dayjs(text).format("DD-MM-YYYY HH:mm")}
        </span>
      ),
    });
    columns.push({
      title: "Updated By",
      dataIndex: "updated_by_user",
      render: (text) => {
        return <span className="text-small">{startCase(text)}</span>;
      },
    });
  }
  if (selectedStatus === "-1") {
    columns.push({
      title: "Rejected At",
      dataIndex: "rejected_at",
      render: (text) => (
        <span className="text-small">
          {dayjs(text).format("DD-MM-YYYY HH:mm")}
        </span>
      ),
    });
    columns.push({
      title: "Rejected By",
      dataIndex: "rejected_by_user",
      render: (text) => {
        return <span className="text-small">{startCase(text)}</span>;
      },
    });
    columns.push({
      title: "Reject Reason",
      dataIndex: "reject_reason",
      render: (text) => {
        return <span className="text-small">{text}</span>;
      },
    });
  }

  if (editPermission || deletePermission || approvePermission) {
    columns.push({
      title: "Actions",
      render: (_, record) => (
        <Space>
          {user && (
            <>
              {editPermission && ["1", "-1"].includes(selectedStatus) && (
                <Tooltip title="Edit">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                </Tooltip>
              )}
              {deletePermission && ["1", "-1"].includes(selectedStatus) && (
                <Popconfirm
                  title="Are you sure to delete this media?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Delete">
                    <Button danger size="small" icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              )}
              {approvePermission && selectedStatus === "0" && (
                <>
                  <Popconfirm
                    title="Are you sure to approve this media?"
                    onConfirm={() => handleApprove(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tooltip title="Approve Media">
                      <Button
                        size="small"
                        icon={<CheckOutlined />}
                        style={{
                          borderColor: "green",
                          color: "green",
                        }}
                      />
                    </Tooltip>
                  </Popconfirm>
                  <RejectButton record={record} handleReject={handleReject} />
                </>
              )}
            </>
          )}
        </Space>
      ),
    });
  }
  return (
    <RequirePermission permission="signage.media_view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Media Library`}</title>
        </Helmet>
        <Title level={3} className="mb-3">
          Media Library
        </Title>

        <Radio.Group
          defaultValue={selectedStatus}
          buttonStyle="solid"
          className="mb-3"
          onChange={(e) => handleApprovalStatus(e)}
        >
          <Radio.Button value="1">Approved</Radio.Button>
          {approvePermission && (
            <Radio.Button value="0">
              Pending {pendingTotal > 0 && `(${pendingTotal})`}
            </Radio.Button>
          )}
          <Radio.Button value="-1">Rejected</Radio.Button>
        </Radio.Group>

        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex gap-4">
            <Input.Search
              placeholder="Search media"
              value={filterText}
              onChange={handleSearch}
              style={{ width: 200 }}
            />

            <Button icon={<FunnelPlotOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              style={{ color: showFilters ? 'green' : 'gray', borderColor: showFilters ? 'green' : 'gray', marginRight: "1rem", width: "30px", height: "30px" }}
            >
              {(activeStatus) && <span style={{
                margin: "0px",
                position: "absolute",
                bottom: "85%",
                left: "85%",
                backgroundColor: "red",
                height: "8px",
                width: "8px",
                borderRadius: "50%",
                zIndex: 1,
              }}>
              </span>}
            </Button>

            {showFilters && <>{selectedStatus === "1" && <div>
              <span className="me-2">Status:</span>
              <Select
                value={activeStatus}
                showSearch
                onChange={(e) => handleStatusChange(e)}
                style={{ width: 200, marginRight: "1rem" }}
                allowClear
              >
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>}</>}
            {selectedStatus === "1" && <div>
              <span className="me-2">Type:</span>
              <Select
                value={selectedType}
                showSearch
                onChange={(e) => handleTypeChange(e)}
                style={{ width: 200, marginRight: "1rem" }}
                allowClear
              >
                <Option value="youtube">Youtube</Option>
                <Option value="video">Video</Option>
                <Option value="url">URL</Option>
                <Option value="image">Image</Option>
              </Select>
            </div>}
          </div>
          {createPermission && (
            <div>
              <Button
                type="primary"
                onClick={() => {
                  setEditingMedia(null);
                  setIsModalOpen(true);
                }}
                style={{ marginRight: "1rem" }}
              >
                Add New Media
              </Button>
              {selectedStatus === "0" && (
                <Button
                  icon={<SyncOutlined />}
                  onClick={() => handleSync()}
                  loading={syncLoading}
                  disabled={syncLoading}
                  style={{ backgroundColor: "#0b59c0ff", color: "white" }}
                >
                  Sync
                </Button>
              )}
            </div>
          )}
        </div>

        <Table
          loading={loading}
          columns={columns}
          dataSource={media}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} media`,
            showSizeChanger: true, // Enables the page size selection dropdown
            pageSizeOptions: ["5", "10", "20", "50", "100"], // Options for the page size selection
          }}
        />
        {isModalOpen && (
          <MediaForm
            isModalOpen={isModalOpen}
            closeModal={() => setIsModalOpen(false)}
            editingMedia={editingMedia}
            handleUpdateList={() => fetchData()}
          />
        )}
        {isPreviewModalOpen && (
          <PreviewMedia
            isModalOpen={isPreviewModalOpen}
            closeModal={() => setIsPreviewModalOpen(false)}
            media={previewMedia}
          />
        )}
      </div>
    </RequirePermission>
  );
};

export default Library;
